import { useCallback, useMemo, useRef, useState } from "react";
import { GoogleMap, Marker, StandaloneSearchBox, useJsApiLoader } from "@react-google-maps/api";

const mapContainerStyle = {
  width: "100%",
  height: "320px",
  borderRadius: "10px",
};

const DEFAULT_CENTER = { lat: 30.3753, lng: 69.3451 };
const libraries = ["places"];

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const CoordinatePicker = ({
  latitude,
  longitude,
  onSelect,
  addressHint = "",
  apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
}) => {
  const mapRef = useRef(null);
  const searchBoxRef = useRef(null);
  const [searchInput, setSearchInput] = useState(addressHint || "");
  const { isLoaded, loadError } = useJsApiLoader({
    id: "coordinate-picker-superadmin",
    googleMapsApiKey: apiKey,
    libraries,
  });

  const markerPosition = useMemo(() => {
    const lat = toNumber(latitude);
    const lng = toNumber(longitude);
    if (lat === null || lng === null) return null;
    return { lat, lng };
  }, [latitude, longitude]);

  const mapCenter = markerPosition || DEFAULT_CENTER;

  const updateCoordinates = useCallback(
    (lat, lng) => {
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      onSelect?.(Number(lat.toFixed(6)), Number(lng.toFixed(6)));
    },
    [onSelect]
  );

  const handleMapClick = useCallback(
    (event) => {
      if (!event?.latLng) return;
      updateCoordinates(event.latLng.lat(), event.latLng.lng());
    },
    [updateCoordinates]
  );

  const handlePlacesChanged = useCallback(() => {
    const places = searchBoxRef.current?.getPlaces?.() || [];
    const firstPlace = places[0];
    const location = firstPlace?.geometry?.location;
    if (!location) return;
    const next = { lat: location.lat(), lng: location.lng() };
    updateCoordinates(next.lat, next.lng);
    if (mapRef.current) {
      mapRef.current.panTo(next);
      mapRef.current.setZoom(16);
    }
  }, [updateCoordinates]);

  if (!apiKey) {
    return (
      <div className="alert alert-warning mb-0">
        Set <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> in <code>SuperAdmin/.env</code> to pick coordinates from map.
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="alert alert-danger mb-0">
        Failed to load Google Maps. Check API key and enabled APIs.
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="d-flex align-items-center justify-content-center border rounded text-muted" style={mapContainerStyle}>
        Loading map...
      </div>
    );
  }

  return (
    <div>
      <div className="mb-2">
        <StandaloneSearchBox
          onLoad={(ref) => {
            searchBoxRef.current = ref;
          }}
          onPlacesChanged={handlePlacesChanged}
        >
          <input
            type="text"
            className="form-control"
            placeholder="Search restaurant location on Google Maps"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </StandaloneSearchBox>
      </div>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={mapCenter}
        zoom={markerPosition ? 15 : 6}
        onLoad={(map) => {
          mapRef.current = map;
        }}
        onClick={handleMapClick}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
          clickableIcons: false,
          gestureHandling: "greedy",
        }}
      >
        {markerPosition && <Marker position={markerPosition} />}
      </GoogleMap>
      <small className="text-muted d-block mt-1">
        Search the address or click on map to set exact restaurant coordinates.
      </small>
    </div>
  );
};

export default CoordinatePicker;
